import { useCallback, useEffect, useState } from 'react'
import Modal from '../../components/Modal'
import Boton from '../../components/Boton'
import CampoFecha from '../../components/CampoFecha'
import SelectorArticulosModal from '../../components/SelectorArticulosModal'
import { listasProveedorApi } from '../../api/compras'
import { fmtFecha } from '../../utils/fecha'
import { colors, text, space } from '../../design/tokens'
import { mensajeError, st } from '../emp900/_helpers'
import { CajaError } from '../emp900/_comun'
import { fmtMoneda } from './_np'

/**
 * Lista de precios del proveedor, desde la Nota de Pedido (Martin, 15 sep 2026:
 * funcionalidad primero, el diseño después). Subís el archivo; lo seguro se
 * vincula solo (código del proveedor, EAN); lo que sugiere la IA se acepta de
 * una vez; lo que no se pudo vincular queda afuera de la lista. Activada, la
 * lista precarga precio y descuentos en los renglones de la NP.
 */
export default function ModalListaPreciosProveedor({ proveedor, onClose, onActivada }) {
  const [vigente, setVigente] = useState(null)
  const [lista, setLista] = useState(null)
  const [archivo, setArchivo] = useState(null)
  const [vigenteDesde, setVigenteDesde] = useState('')
  const [trabajando, setTrabajando] = useState('')
  const [error, setError] = useState('')
  const [vinculando, setVinculando] = useState(null) // item_id

  const cargar = useCallback(async () => {
    try {
      const { data } = await listasProveedorApi.listar(proveedor.id)
      const ls = data.listas || []
      setVigente(ls.find(l => l.estado === 'vigente') || null)
      const enRevision = ls.find(l => l.estado === 'revision')
      if (enRevision) { const { data: d } = await listasProveedorApi.obtener(enRevision.id); setLista(d.lista) }
    } catch (e) { setError(mensajeError(e)) }
  }, [proveedor.id])
  useEffect(() => { cargar() }, [cargar])

  const accion = async (etiqueta, fn, despues) => {
    setTrabajando(etiqueta); setError('')
    try { const { data } = await fn(); setLista(data.lista); despues?.(data.lista) } catch (e) { setError(mensajeError(e)) } finally { setTrabajando('') }
  }

  const subir = () => accion('Leyendo la lista…', () => listasProveedorApi.subir(proveedor.id, archivo, vigenteDesde), () => setArchivo(null))
  const r = lista?.resumen
  const cmp = lista?.comparacion

  return (
    <Modal title={`Lista de precios — ${proveedor.nombre}`} onClose={onClose} maxWidth="980px">
      <p style={{ ...st.info, marginTop: 0 }}>
        {vigente
          ? <>Vigente desde <b>{fmtFecha(vigente.vigente_desde)}</b> · {vigente.vinculados} artículos · {vigente.archivo_nombre}</>
          : 'Este proveedor todavía no tiene lista vigente.'}
      </p>

      {!lista && (
        <div style={{ ...st.card, display: 'flex', gap: space.md, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={st.cardTitulo}>Subir lista nueva</div>
            <input type="file" accept=".xlsx,.xls,.csv,.txt,.pdf,.jpg,.jpeg,.png,.webp" onChange={e => setArchivo(e.target.files?.[0] || null)} />
            <div style={{ fontSize: text.sec, color: colors.textMeta, marginTop: space.xs }}>Excel, CSV, PDF o foto. Las planillas se leen por sus encabezados; PDF y fotos los lee la IA.</div>
          </div>
          <div style={{ width: 180 }}>
            <div style={{ fontSize: text.meta, color: colors.textSec, marginBottom: 4 }}>VIGENTE DESDE (vacío = al activar)</div>
            <CampoFecha style={st.input} value={vigenteDesde} onChange={v => setVigenteDesde(v?.target ? v.target.value : v)} />
          </div>
          <Boton variante="primary" disabled={!archivo || !!trabajando} onClick={subir}>{trabajando || 'Subir y leer'}</Boton>
        </div>
      )}

      {error && <CajaError>{error}</CajaError>}

      {lista && (
        <div>
          <div style={{ ...st.card, marginBottom: space.md }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: space.md, flexWrap: 'wrap' }}>
              <div>
                <div style={st.cardTitulo}>{lista.archivo_nombre} · {lista.filas} renglones</div>
                <div style={{ fontSize: text.body, color: colors.text }}>
                  Vinculados <b>{r.vinculados}</b> ({r.por_codigo} por código · {r.por_ean} por EAN · {r.por_ia} IA · {r.manuales} a mano)
                  {' · '}Sugeridos por la IA <b>{r.sugeridos}</b>{' · '}Sin vincular <b>{r.sin_vincular}</b>{r.ignorados ? <> · Ignorados {r.ignorados}</> : null}
                </div>
                {cmp && <div style={{ fontSize: text.sec, color: colors.textSec, marginTop: space.xs }}>Contra la lista del {fmtFecha(cmp.contra_vigente_desde)}: {cmp.aumentos} aumentos · {cmp.bajas} bajas · {cmp.iguales} iguales · {cmp.nuevos} nuevos · {cmp.salen} salen</div>}
                {lista.columnas && <div style={{ fontSize: text.sec, color: colors.textMeta, marginTop: space.xs }}>{lista.columnas}</div>}
                {lista.ia_error && <div style={{ fontSize: text.sec, color: colors.warn, marginTop: space.xs }}>⚠ {lista.ia_error}</div>}
              </div>
              <div style={{ display: 'flex', gap: space.sm, alignItems: 'flex-start' }}>
                <Boton variante="destructive" sm disabled={!!trabajando} onClick={() => accion('Descartando…', () => listasProveedorApi.descartar(lista.id), () => { setLista(null); cargar() })}>Descartar</Boton>
                <Boton variante="primary" sm disabled={!!trabajando || r.sugeridos > 0 || r.vinculados === 0} title={r.sugeridos > 0 ? 'Aceptá o rechazá las sugerencias de la IA' : ''}
                  onClick={() => accion('Activando…', () => listasProveedorApi.activar(lista.id), () => { onActivada?.(); onClose() })}>Activar lista</Boton>
              </div>
            </div>
          </div>

          {lista.sugeridos.length > 0 && (
            <Seccion titulo={`La IA sugiere (${lista.sugeridos.length})`} accion={<Boton variante="success" sm disabled={!!trabajando} onClick={() => accion('Aceptando…', () => listasProveedorApi.aceptarSugerencias(lista.id))}>✓ Aceptar todas</Boton>}>
              <Tabla cols={['Renglón del proveedor', 'Artículo de LGR sugerido', 'Precio', '']} filas={lista.sugeridos.map(it => [
                <Proveedor key="p" it={it} />,
                <span key="s"><b style={{ fontFamily: 'monospace' }}>{it.sugerido?.codigo_alfa}</b> · {it.sugerido?.descripcion}</span>,
                fmtMoneda(it.precio_lista),
                <span key="a" style={{ whiteSpace: 'nowrap' }}>
                  <Boton variante="ghost" sm onClick={() => accion('', () => listasProveedorApi.rechazarSugerencia(lista.id, it.item_id))}>✕ No es</Boton>
                </span>,
              ])} />
            </Seccion>
          )}

          {lista.sin_vincular.length > 0 && (
            <Seccion titulo={`Sin vincular (${lista.sin_vincular.length}) — quedan afuera de la lista salvo que los vincules`}>
              <Tabla cols={['Renglón del proveedor', 'Precio', '']} filas={lista.sin_vincular.map(it => [
                <span key="p" style={{ opacity: it.ignorado ? 0.5 : 1 }}><Proveedor it={it} /></span>,
                fmtMoneda(it.precio_lista),
                <span key="a" style={{ whiteSpace: 'nowrap', display: 'flex', gap: space.xs }}>
                  <Boton variante="secondary" sm onClick={() => setVinculando(it.item_id)}>🔍 Vincular</Boton>
                  <Boton variante="ghost" sm onClick={() => accion('', () => listasProveedorApi.ignorar(lista.id, it.item_id, !it.ignorado))}>{it.ignorado ? 'Volver a incluir' : 'Ignorar'}</Boton>
                </span>,
              ])} />
            </Seccion>
          )}

          {lista.cambios.length > 0 && (
            <Seccion titulo={`Cambios de precio (${lista.cambios.length})`}>
              <Tabla cols={['Artículo', 'Anterior', 'Nuevo', 'Var.']} filas={lista.cambios.map(c => [
                <span key="a"><b style={{ fontFamily: 'monospace' }}>{c.articulo?.codigo_alfa}</b> · {c.articulo?.descripcion}</span>,
                c.precio_anterior == null ? '—' : fmtMoneda(c.precio_anterior),
                fmtMoneda(c.precio_nuevo),
                c.tipo === 'nuevo' ? 'nuevo' : <span key="v" style={{ color: c.variacion_pct > 0 ? colors.err : colors.ok }}>{c.variacion_pct > 0 ? '+' : ''}{c.variacion_pct}%</span>,
              ])} />
            </Seccion>
          )}

          {lista.salen.length > 0 && (
            <Seccion titulo={`Salen de la lista (${lista.salen.length}) — dejan de tener precio de este proveedor`}>
              <Tabla cols={['Artículo', 'Precio anterior']} filas={lista.salen.map(s => [
                <span key="a"><b style={{ fontFamily: 'monospace' }}>{s.articulo?.codigo_alfa}</b> · {s.articulo?.descripcion}</span>,
                fmtMoneda(s.precio_anterior),
              ])} />
            </Seccion>
          )}
        </div>
      )}

      {vinculando && (
        <SelectorArticulosModal modo="simple" onClose={() => setVinculando(null)}
          onSelect={a => { const itemId = vinculando; setVinculando(null); accion('', () => listasProveedorApi.vincular(lista.id, itemId, a.id)) }} />
      )}
    </Modal>
  )
}

function Seccion({ titulo, accion, children }) {
  return (
    <div style={{ ...st.card, marginBottom: space.md }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.sm }}>
        <div style={st.cardTitulo}>{titulo}</div>{accion}
      </div>
      {children}
    </div>
  )
}

function Tabla({ cols, filas }) {
  return (
    <div style={{ ...st.tableWrap, maxHeight: 280, overflowY: 'auto' }}>
      <table style={st.table}>
        <thead><tr>{cols.map((c, i) => <th key={i} style={st.th}>{c}</th>)}</tr></thead>
        <tbody>{filas.map((f, i) => <tr key={i} style={st.tr}>{f.map((v, j) => <td key={j} style={st.td}>{v}</td>)}</tr>)}</tbody>
      </table>
    </div>
  )
}

function Proveedor({ it }) {
  return (
    <span>
      {it.descripcion || '—'}
      <div style={{ fontSize: text.meta, color: colors.textMeta, fontFamily: 'monospace' }}>
        {[it.codigo_proveedor && `cód. ${it.codigo_proveedor}`, it.ean && `EAN ${it.ean}`, `fila ${it.fila}`].filter(Boolean).join(' · ')}
      </div>
    </span>
  )
}
